/**
 * What a pot weighs when it comes off the heat, before anyone has weighed it.
 *
 * A recipe's macros are divided by its finished weight, so that number decides
 * every portion logged from it. The honest source is a scale — which is why the
 * field stays editable and the screen keeps asking — but a recipe is written
 * before the dish is out of the oven, and until it is weighed the app has to
 * assume something. Assuming "the same as the raw ingredients" is the one
 * assumption that is wrong for almost every cooked dish: a cake that lost a
 * tenth of its weight as steam reads a tenth light in every slice.
 *
 * These are whole-dish figures, not the per-ingredient retention factors in
 * `cooking.ts`. A chicken breast in a pan loses a quarter of its weight; a tray
 * of batter does not, because most of its water is bound up in starch and egg.
 * Mixing the two sets is how a cake ends up estimated like a steak.
 */

export type RecipeCooking = 'unweighed' | 'none' | 'baked' | 'stovetop' | 'fried'

export interface RecipeCookingProfile {
  label: string
  /** Finished weight ÷ raw weight. Null means "no estimate, use the raw total". */
  yieldFactor: number | null
  hint: string
}

export const RECIPE_COOKING: Readonly<Record<RecipeCooking, RecipeCookingProfile>> = {
  unweighed: {
    label: 'Nežinau',
    yieldFactor: null,
    hint: 'Kol kas laikoma, kad sveria tiek pat, kiek žali produktai.',
  },
  none: {
    label: 'Nekaitinta',
    yieldFactor: 1,
    hint: 'Salotos, užtepėlės, desertai be kepimo — svoris nekinta.',
  },
  baked: {
    label: 'Orkaitėje',
    yieldFactor: 0.88,
    hint: 'Pyragai ir apkepai netenka maždaug dešimtadalio svorio garais.',
  },
  stovetop: {
    label: 'Virta puode',
    yieldFactor: 0.95,
    hint: 'Troškiniai ir košės po dangčiu netenka nedaug. Sriubą su vandeniu pasverkite.',
  },
  fried: {
    label: 'Keptuvėje',
    yieldFactor: 0.78,
    hint: 'Atviroje keptuvėje išgaruoja daugiausia.',
  },
}

export const RECIPE_COOKING_ORDER: readonly RecipeCooking[] = [
  'unweighed',
  'none',
  'baked',
  'stovetop',
  'fried',
]

/** The finished weight to offer, or null when there is nothing better than the raw total. */
export function estimatedCookedWeight(rawTotalG: number, method: RecipeCooking): number | null {
  const factor = RECIPE_COOKING[method].yieldFactor
  if (factor == null || rawTotalG <= 0) return null
  return Math.round(rawTotalG * factor)
}
